// Editable fields of a journal line and how the lines table groups them.
// Amounts are not here: on lines built from the invoice / PO they must match the
// HSBC payment. Only manual lines carry their own amount (AMOUNT_KEYS).
//
// Account type / Offset account type options come from the App Settings master
// ERP_ACCOUNT_TYPES (sent as `account_types` with the workbench).
export const FIELDS = [
  { key: "account_type", label: "Account type", ledgerOnly: true, accountType: true, width: 140 },
  // The vendor / employee line takes a typed account too (defaults to the header code).
  { key: "account", label: "Account", width: 130 },
  { key: "offset_account_type", label: "Offset account type", accountType: true, width: 160 },
  { key: "offset_account", label: "Offset account", width: 150 },
  { key: "invoice", label: "Invoice no.", width: 130 },
  { key: "txt", label: "Transaction text EN", wide: true, width: 280 },
  { key: "peb_txt", label: "Transaction text VN", wide: true, width: 300 },
  { key: "dim_department", label: "Department", width: 120 },
  { key: "dim_cost_center", label: "Cost center", width: 120 },
  { key: "dim_purpose", label: "Job", width: 120 },
  { key: "dim_category", label: "Category", width: 120 },
  // AX "Employee code" dimension — its own AX list, not the requester's HR code (kind employee_code).
  { key: "dim_employee_code", label: "Employee code", kind: 'dim_employee_code', width: 130 },
  { key: "tax_group", label: "Sales tax group", width: 130 },
  { key: "tax_item_group", label: "Item sales tax group", taxGroup: true, width: 130 },
  { key: "payment_terms_id", label: "Terms of payment", kind: 'payment_terms_id', width: 140 },
  { key: "due_date", label: "Due date", date: true, width: 150 },
  { key: "document_num", label: "Document", kind: 'document_num', width: 130 },
  { key: "document_date", label: "Invoice date", date: true, width: 150 },
];

export const FIELD_BY_KEY = Object.fromEntries(FIELDS.map((f) => [f.key, f]));

// Every column the viewer can drag wider or narrower (default widths): the Line column,
// each field and the account Name. Kept per browser (useColumnWidths).
export const LINE_COLUMN_WIDTH = 240;
export const RESIZABLE_WIDTHS = {
  line: LINE_COLUMN_WIDTH,
  ...Object.fromEntries(FIELDS.map((f) => [f.key, f.width])),
  account_name: 200,
};

// `collapsible` groups shrink to one summary column so a journal with many
// lines stays readable; the account & text group is what every line needs.
//
// Besides editable fields, a group may hold the read-only `account_name`
// ("Name" — the name of the account picked) and the `debit` / `credit` amounts.
// Journal type API reads left to right the way the accountant books it:
// account → name → invoice → text → amounts → offset. Other journal types keep
// the amounts pinned after the Line column.
export const COLUMNS = {
  account_name: { key: 'account_name', label: 'Name', display: true, width: 200 },
  debit: { key: 'debit', label: 'Debit', amount: true },
  credit: { key: 'credit', label: 'Credit', amount: true },
};

const TAIL_GROUPS = [
  { id: 'dims', label: 'Dimensions', collapsible: true, keys: ['dim_department', 'dim_cost_center', 'dim_purpose', 'dim_category', 'dim_employee_code'] },
  { id: 'tax', label: 'Sales tax', collapsible: true, keys: ['tax_group', 'tax_item_group'] },
];

// API and APP read the same way (user, 2026-10-17); other journal types keep the
// amounts pinned after the Line column.
export const layoutFor = (journalType) => (['API', 'APP'].includes(journalType)
  ? {
    pinnedAmounts: false,
    groups: [
      { id: 'main', label: 'Accounts & description',
        keys: ['account_type', 'account', 'account_name', 'invoice', 'txt', 'peb_txt', 'debit', 'credit', 'offset_account_type', 'offset_account'] },
      ...TAIL_GROUPS,
      { id: 'invoice', label: 'Invoice', collapsible: true, keys: ['payment_terms_id', 'due_date', 'document_num', 'document_date'] },
    ],
  }
  : {
    pinnedAmounts: true,
    groups: [
      { id: 'main', label: 'Accounts & description', keys: ['account_type', 'account', 'offset_account_type', 'offset_account', 'txt', 'peb_txt'] },
      ...TAIL_GROUPS,
      { id: 'invoice', label: 'Invoice', collapsible: true, keys: ['invoice', 'payment_terms_id', 'due_date', 'document_num', 'document_date'] },
    ],
  });

export const columnDef = (key) => FIELD_BY_KEY[key] || COLUMNS[key];

// APP booked as in AX: a line whose bank sits on the "Bank payment" line has no offset
// of its own — the bank is changed on that line's Account.
const BANK_LINE_OFFSET = ["offset_account", "offset_account_type"];
export const offsetOnBankLine = (line, field) => !!line.offset_by_bank_line && BANK_LINE_OFFSET.includes(field.key);

export const isFieldEditable = (line, field) =>
  !(field.ledgerOnly && (line.account_type === "Vendor" || line.counterparty_line)) && !offsetOnBankLine(line, field);

export const editableFields = (line) => FIELDS.filter((f) => isFieldEditable(line, f));

// Dòng gắn theo `key` (vd "exp:123", "vat:HD-01") — thêm/bớt dòng hoá đơn không làm
// giá trị đã điền nhảy sang dòng khác. Dữ liệu cũ chưa có key thì dùng số dòng.
export const lineId = (l) => l.key || String(l.line_num);

export const AMOUNT_KEYS = ['debit', 'credit'];

// Manual lines keep their amount in the editable values; built lines do not.
export const toValues = (lines = []) =>
  Object.fromEntries(lines.map((l) => [lineId(l), {
    ...Object.fromEntries(FIELDS.map((f) => [f.key, l[f.key] ?? ""])),
    ...(l.manual ? Object.fromEntries(AMOUNT_KEYS.map((k) => [k, Number(l[k]) ? String(Number(l[k])) : ""])) : {}),
  }]));

// Fallback when an older API answers without the master list.
export const DEFAULT_ACCOUNT_TYPES = [
  { value: 'Ledger', label: 'Ledger', entry_kind: 'ledger_account' },
  { value: 'Customer', label: 'Customer', entry_kind: 'customer_account' },
  { value: 'Vendor', label: 'Vendor', entry_kind: 'vendor_account' },
  { value: 'Project', label: 'Project', entry_kind: 'project_account' },
  { value: 'FixedAssets', label: 'Fixed assets', entry_kind: 'fixed_asset_account' },
  { value: 'Bank', label: 'Bank', entry_kind: 'bank_ledger_account' },
];

// Fallback when an older API answers without the item sales tax group master.
export const DEFAULT_ITEM_SALES_TAX_GROUPS = [
  { value: '00', label: 'VAT 00%', vat_account: '133105' },
  { value: '05', label: 'VAT 05%', vat_account: '133101' },
  { value: '08', label: 'VAT 8%', vat_account: '133104' },
  { value: '10', label: 'VAT 10%', vat_account: '133102' },
  { value: 'NV', label: 'Non VAT', vat_account: '' },
  { value: 'OT', label: 'VAT Other', vat_account: '133106' },
];

// The VAT line of an invoice (key "vat:<invoice>", or "vat" on a PO journal).
export const isVatLine = (line) => /^vat(:|$)/.test(String(line.key || ''));

export const entryKindFor = (types, value) =>
  types.find((t) => t.value === value)?.entry_kind || 'ledger_account';

export const accountTypeLabel = (types, value) => types.find((t) => t.value === value)?.label || value || '—';

// Manual lines sit after the built ones; their numbers follow on.
export const newManualKey = () => `man:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// Built lines first (in their order), then manual ones; numbered 1..n as the server
// will number them after a save — lines may have been removed or added.
export const renumber = (lines) => {
  // base_num = the built line's place before any removal (from the server).
  const position = (l) => l.base_num ?? l.line_num ?? 0;
  const built = lines.filter((l) => !l.manual).sort((a, b) => position(a) - position(b));
  return [...built, ...lines.filter((l) => l.manual)].map((l, i) => ({ ...l, line_num: i + 1 }));
};

export const money = (v) => {
  const amount = Number(v) || 0;
  return amount === 0 ? '—' : amount.toLocaleString('en-US');
};

// Offset accounts of the HSBC online-payment route (HSBC01VND, HSBC01USD…): paid
// through HSBC right after the BOD signs. Reads what is on screen, saved or not.
export const HSBC_OFFSET_PREFIX = "HSBC";
// The PEB bank the payment leaves from: the offset account of a line, or — when the
// bank is booked on its own Credit line (APP "Bank payment" line) — that line's account.
export const hsbcOffsetAccounts = (lines = [], values = {}) => [...new Set(lines
  .flatMap((l) => {
    const v = values[lineId(l)] || {};
    const offset = v.offset_account ?? l.offset_account;
    return l.bank_line ? [v.account ?? l.account, offset] : [offset];
  })
  .map((code) => String(code ?? "").trim().toUpperCase())
  .filter((code) => code.startsWith(HSBC_OFFSET_PREFIX)))];
